import { Checkbox, Box } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import EditActionButton from 'containers/Reports/shared/EditActionButton';
import { ReportsTableDataCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { useUserObject } from 'store/useUserStore';
import { formatDistanceUnit, formatCurrency, formatYearMonthDayTime } from 'utils/format';
import { isDistanceInMiles, convertSecondsToMinutesRoundDown } from 'utils/helpers';

interface IEmployeeBodyTableProps {
  data: any;
  checkedTimesheet: string[];
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, timeSheetId: string) => void;
}

const EmployeeGroupTableBody: React.FC<IEmployeeBodyTableProps> = ({
  data,
  checkedTimesheet,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation(['common', 'reports']);

  const userStore = useUserObject();
  useStickyHeader();

  return data.users.map((item: any) => {
    return (
      <React.Fragment key={item?.user_id}>
        {Array.isArray(item?.data?.timesheets) && item.data.timesheets.length
          ? item.data.timesheets.map((timesheet: any, index: number) => (
              <ReportsTableRow key={timesheet?.id} minWidth="100%" width="fit-content">
                <ReportsTableDataCell width="30px" justifyContent="center" flex="30 0 auto">
                  <Checkbox
                    isChecked={checkedTimesheet.includes(timesheet?.id)}
                    onChange={e => handleCheckboxChange(e, timesheet?.id)}
                    colorScheme="primary"
                  />
                </ReportsTableDataCell>
                <ReportsTableDataCell>
                  {index === 0 ? timesheet?.first_name + ' ' + timesheet?.last_name : ''}
                </ReportsTableDataCell>
                {userStore?.companyInfo?.show_employee_id ? (
                  <ReportsTableDataCell width="120px">{index === 0 ? timesheet?.employee_id : ''}</ReportsTableDataCell>
                ) : null}
                <ReportsTableDataCell width="250px">
                  <Box display="flex" flexDirection="column">
                    {timesheet?.job?.name || '- '}
                    {timesheet?.task?.name ? <Box>{'/' + timesheet?.task?.name}</Box> : null}
                  </Box>
                </ReportsTableDataCell>
                <ReportsTableDataCell width="200px">
                  {timesheet?.clock_in?.local_time ? formatYearMonthDayTime(timesheet?.clock_in?.local_time) : '-'}
                  {' - '}
                  <br />
                  {timesheet?.clock_out?.local_time ? formatYearMonthDayTime(timesheet?.clock_out?.local_time) : ''}
                </ReportsTableDataCell>
                <ReportsTableDataCell width="150px">
                  {convertSecondsToMinutesRoundDown(timesheet?.paid_break_in_seconds)}
                </ReportsTableDataCell>
                <ReportsTableDataCell width="150px">
                  {convertSecondsToMinutesRoundDown(timesheet?.unpaid_break_in_seconds)}
                </ReportsTableDataCell>
                <ReportsTableDataCell color="secondary.700" width="120px">
                  {timesheet?.payrolls?.duration}
                </ReportsTableDataCell>
                {userStore?.companyInfo?.show_mileage ? (
                  <ReportsTableDataCell width="140px">
                    {!isNaN(timesheet?.mileage) ? formatDistanceUnit(timesheet?.mileage) : '-'}
                  </ReportsTableDataCell>
                ) : null}
                {userStore?.companyInfo?.weekly_overtime ? (
                  <>
                    <ReportsTableDataCell width="120px">
                      {timesheet?.payrolls?.regular_time || '-'}
                    </ReportsTableDataCell>
                    <ReportsTableDataCell width="120px">
                      {timesheet?.payrolls?.regular_cost || '-'}
                    </ReportsTableDataCell>
                  </>
                ) : null}
                {userStore?.companyInfo?.daily_overtime ? (
                  <ReportsTableDataCell color="secondary.600" width="120px">
                    {timesheet?.payrolls?.overtime}
                  </ReportsTableDataCell>
                ) : null}
                {userStore?.companyInfo?.daily_double_overtime ? (
                  <ReportsTableDataCell color="red.700" width="120px">
                    {timesheet?.payrolls?.double_overtime}
                  </ReportsTableDataCell>
                ) : null}
                {userStore?.companyInfo?.show_timesheet_notes ? (
                  <ReportsTableDataCell>{timesheet?.notes}</ReportsTableDataCell>
                ) : null}
                <EditActionButton timesheetId={timesheet?.id} withoutTd={false} />
              </ReportsTableRow>
            ))
          : null}

        {item?.data?.total ? (
          <ReportsTableRow bg="primary.50" minWidth="100%" width="fit-content" className="js-row-total">
            <ReportsTableDataCell width="290px" fontWeight="semibold">
              {t('reports:overtimeHours')}: {item?.data.total?.daily_overtime_hours || '-'}
            </ReportsTableDataCell>
            {userStore?.companyInfo?.include_premium_pay ? (
              <ReportsTableDataCell fontWeight="semibold">
                {t('reports:premiumPayHours')}: {item?.data.total?.premium_pay_hours || '-'}
              </ReportsTableDataCell>
            ) : null}
            <ReportsTableDataCell fontWeight="semibold" width="290px">
              {t('reports:doubleOvertimeHours')}: {item?.data.total?.double_overtime_hours || '-'}
            </ReportsTableDataCell>
            <ReportsTableDataCell width="290px" fontWeight="semibold">
              {t('reports:totalHours')}: {item?.data.total?.total_hours || '-'}
            </ReportsTableDataCell>
            <ReportsTableDataCell width="290px" fontWeight="semibold">
              {t('reports:totalHoursCost')}:{' '}
              {item?.data?.total?.total_cost ? formatCurrency(item?.data?.total?.total_cost) : '-'}
            </ReportsTableDataCell>
            {userStore?.companyInfo?.show_mileage ? (
              <>
                <ReportsTableDataCell width="297px" fontWeight="semibold">
                  {isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers')}:{' '}
                  {item?.data?.total?.total_mileage ? formatDistanceUnit(+item?.data?.total?.total_mileage) : '-'}
                </ReportsTableDataCell>
                <ReportsTableDataCell width="297px" fontWeight="semibold">
                  {isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost')}:{' '}
                  {item?.data?.total?.total_mileage_cost ? formatCurrency(item?.data?.total?.total_mileage_cost) : '-'}
                </ReportsTableDataCell>
              </>
            ) : null}
          </ReportsTableRow>
        ) : null}
      </React.Fragment>
    );
  });
};
export default EmployeeGroupTableBody;
